<template>
  <div :class="['iniciarSesion-container', { 'dark-theme': isDarkMode }]">
    <!-- Toggle de tema -->
    <div class="theme-toggle">
      <button @click="toggleTheme" class="theme-toggle-btn" :title="isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'">
        <i :class="isDarkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'"></i>
      </button>
    </div>

    <div class="row min-vh-100 g-0">
      <!-- Lado izquierdo - Imagen / branding -->
      <div class="col-md-6 welcome-side">
        <div class="welcome-content">
          <div class="image-placeholder">
            <div class="welcome-image-placeholder">
              <div class="placeholder-content">
                <i class="bi bi-building-check"></i>
                <p>SICECOM</p>
              </div>
            </div>
          </div>
          <h2 class="welcome-title">Bienvenido</h2>
          <p class="welcome-text">Portal de clientes</p>
        </div>
      </div>

      <!-- Lado derecho - iniciarSesion -->
      <div class="col-md-6 iniciarSesion-side">
        <div class="iniciarSesion-form-container">
          <div class="text-center mb-4">
            <img src="/logo_b.png" alt="SICECOM" class="iniciarSesion-logo-large" />
          </div>

          <div class="iniciarSesion-card">
            <form @submit.prevent="onSubmit" novalidate>
              <div class="mb-3">
                <label class="form-label" for="email">Correo</label>
                <input
                  id="email"
                  v-model.trim="email"
                  type="email"
                  class="form-control iniciarSesion-input"
                  :class="{ 'is-invalid': errorMessage }"
                  placeholder="cliente@empresa.com"
                  autocomplete="email"
                  required
                  :disabled="loading"
                />
              </div>

              <div class="mb-3">
                <label class="form-label" for="password">Contraseña</label>
                <div class="input-group">
                  <input
                    id="password"
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control iniciarSesion-input"
                    :class="{ 'is-invalid': errorMessage }"
                    placeholder="••••••••"
                    autocomplete="current-password"
                    required
                    :disabled="loading"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary password-toggle"
                    @click="togglePasswordVisibility"
                    :disabled="loading"
                    :title="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                  >
                    <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
              </div>

              <div class="text-end mb-3">
                <a href="#" @click.prevent="goForgot" class="forgot-password-link">¿Olvidaste tu contraseña?</a>
              </div>

              <div v-if="errorMessage" class="alert alert-danger d-flex align-items-center mb-4" role="alert">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                <div>{{ errorMessage }}</div>
              </div>

              <button type="submit" class="btn iniciarSesion-btn w-100 py-3" :disabled="loading">
                <div v-if="loading" class="d-flex align-items-center justify-content-center">
                  <div class="spinner-border spinner-border-sm me-2" role="status">
                    <span class="visually-hidden">Cargando...</span>
                  </div>
                  Iniciando sesión...
                </div>
                <span v-else>Iniciar Sesión</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import auth from '@/services/servicioAutenticacion';
import themeManager from '@/store/theme';
import { construirUrlEmpresa, esSubdominioLogin } from '@/utils/subdominio';

export default {
  name: 'InicioSesionCliente',
  data() {
    return {
      email: '',
      password: '',
      errorMessage: '',
      showPassword: false,
      loading: false
    };
  },
  computed: {
    isDarkMode() {
      return themeManager.isDarkMode;
    }
  },
  mounted() {
    // Inicializar el tema desde localStorage
    themeManager.init();
  },
  methods: {
    toggleTheme() {
      themeManager.toggleTheme();
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    async onSubmit() {
      if (this.loading) return; // Prevent double submit
      this.errorMessage = '';
      if (!this.email || !this.password) {
        this.errorMessage = 'Ingrese correo y contraseña';
        return;
      }
      if (!navigator.onLine) {
        this.errorMessage = 'Sin conexión a internet';
        return;
      }
      this.loading = true;
      try {
        // Inicio de sesión contra /autenticacion/cliente/iniciar (api base ya en api.js)
        await auth.iniciarSesion(this.email, this.password);
        // Intento opcional de /autenticacion/cliente/perfil (no bloquear si falla)
        try { await auth.obtenerPerfil(); } catch (e) { console.debug('[cliente iniciarSesion] obtenerPerfil falló', e); }

        const redirect = this.$route.query?.redireccion || '/';

        // ── Redirección cross-subdomain ──────────────────────────────────────
        // Tras el login en app.sicecom.com redirigimos a {ruc}.sicecom.com
        // pasando los tokens en el hash para hidratar el localStorage del subdominio.
        // En localhost construirUrlEmpresa devuelve null y se hace push interno.
        if (esSubdominioLogin() && !this.$store.state.user?.is_super_admin) {
          const ruc         = this.$store.state.user?.company?.ruc;
          const tokenAcceso = localStorage.getItem('token_acceso_cliente');
          const tokenRefr   = localStorage.getItem('token_refresco_cliente');
          if (ruc && tokenAcceso) {
            const url = construirUrlEmpresa(ruc, tokenAcceso, tokenRefr, redirect);
            if (url) {
              window.location.replace(url);
              return;
            }
          }
        }
        // ─────────────────────────────────────────────────────────────────────

        this.$router.replace(redirect);
      } catch (e) {
        const status = e?.response?.status;
        let msg = e?.response?.data?.error || e?.message || 'No se pudo iniciar sesión';
        if (status === 401) msg = 'Credenciales inválidas';
        else if (status === 429) msg = 'Demasiados intentos. Intente luego';
        else if (status === 500) msg = 'Error del servidor';
        else if (e.code === 'ERR_NETWORK') msg = 'No se pudo conectar con el servidor';
        this.errorMessage = msg;
      } finally {
        this.loading = false;
      }
    },
    goForgot() {
      this.$router.push('/recuperar-clave');
    }
  }

};
</script>

<style>
/* Variables CSS para temas - Mismas del admin */
.iniciarSesion-container {
  --primary-color: #1173d4;
  --primary-hover: #0f5fa3;
  --bg-light: #f6f7f8;
  --bg-dark: #101922;
  --text-primary: #334155;
  --text-secondary: #64748b;
  --border-color: #e2e8f0;
  --card-bg: #ffffff;
  --card-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --backdrop-blur: blur(8px);
  --success-color: #10b981;
  --warning-color: #f59e0b;
  --danger-color: #ef4444;
  --info-color: #3b82f6;
}

/* Tema oscuro - Mismo del admin */
.iniciarSesion-container.dark-theme {
  --bg-light: #101922;
  --bg-dark: #0f172a;
  --text-primary: #f1f5f9;
  --text-secondary: #cbd5e1;
  --border-color: #334155;
  --card-bg: #1e293b;
  --card-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Toggle de tema - Mismo del admin */
.theme-toggle {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.theme-toggle-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: var(--card-bg);
  color: var(--text-primary);
  box-shadow: var(--card-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.theme-toggle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.theme-toggle-btn:active {
  transform: translateY(0) scale(0.95);
}

/* Indicador visual para el tema actual */
.theme-toggle-btn::before {
  content: '';
  position: absolute;
  top: -3px;
  right: -3px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--primary-color);
  opacity: 0;
  transition: all 0.3s ease;
}

.dark-theme .theme-toggle-btn::before {
  opacity: 1;
  background: #fbbf24;
}

.theme-toggle-btn:hover::before {
  opacity: 0;
}

/* Lado izquierdo - Bienvenida */
.welcome-side {
  background: linear-gradient(135deg, var(--primary-color) 0%, #3b82f6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  transition: all 0.3s ease;
}

/* Modo oscuro para el lado izquierdo */
.iniciarSesion-container.dark-theme .welcome-side {
  background: linear-gradient(135deg, var(--bg-light) 0%, var(--bg-dark) 100%);
}

.welcome-content {
  text-align: center;
  color: white;
  position: relative;
  z-index: 1;
}

.image-placeholder {
  margin-bottom: 2rem;
}

.welcome-image {
  max-width: 300px;
  width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease;
}

.welcome-image:hover {
  transform: translateY(-5px);
}

/* Placeholder de imagen de bienvenida */
.welcome-image-placeholder {
  width: 300px;
  height: 200px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  margin: 0 auto;
}

.welcome-image-placeholder:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.15);
}

.placeholder-content {
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
}

.placeholder-content i {
  font-size: 4rem;
  margin-bottom: 1rem;
  display: block;
  color: rgba(255, 255, 255, 0.8);
}

.placeholder-content p {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: 2px;
}

.welcome-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.welcome-text {
  font-size: 1.2rem;
  opacity: 0.9;
  font-weight: 400;
}

/* Lado derecho - iniciarSesion */
.iniciarSesion-side {
  background: var(--bg-light);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  transition: all 0.3s ease;
}

.iniciarSesion-form-container {
  width: 100%;
  max-width: 400px;
}

.iniciarSesion-logo-large {
  width: 250px;
  height: auto;
  object-fit: contain;
  margin-bottom: 2rem;
}

/* Tarjeta de iniciarSesion */
.iniciarSesion-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--card-shadow);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

/* Modo oscuro para la tarjeta de iniciarSesion */
.dark-theme .iniciarSesion-card {
  background: #374151;
  border-color: #4b5563;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2);
}

.iniciarSesion-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 32px 64px -12px rgba(0, 0, 0, 0.25);
}

/* Formulario - Mismos estilos del admin */
.iniciarSesion-input {
  background: var(--card-bg) !important;
  border: 2px solid var(--border-color) !important;
  color: var(--text-primary) !important;
  font-size: 0.875rem;
  padding: 0.875rem 1rem !important;
  border-radius: 8px !important;
  transition: all 0.3s ease !important;
}

.iniciarSesion-input:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 0.2rem rgba(17, 115, 212, 0.25) !important;
  background: var(--card-bg) !important;
}

.iniciarSesion-input::placeholder {
  color: var(--text-secondary) !important;
}

/* Toggle de contraseña */
.password-toggle {
  background: var(--card-bg) !important;
  border: 2px solid var(--border-color) !important;
  border-left: none !important;
  color: var(--text-secondary) !important;
  border-radius: 0 8px 8px 0 !important;
  transition: all 0.3s ease !important;
  padding: 0.875rem 1rem !important;
}

.password-toggle:hover {
  background: var(--bg-light) !important;
  color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
}

.password-toggle:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 0.2rem rgba(17, 115, 212, 0.25) !important;
  background: var(--card-bg) !important;
}

.password-toggle i {
  font-size: 1rem;
}

/* Input group para contraseña */
.input-group .iniciarSesion-input {
  border-radius: 8px 0 0 8px !important;
}

.input-group .iniciarSesion-input:focus {
  z-index: 3;
}

/* Labels - Mismas del admin */
.form-label {
  color: var(--text-primary) !important;
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  margin-bottom: 0.5rem !important;
  display: block !important;
}

/* Botón de iniciarSesion - Mismo del admin */
.iniciarSesion-btn {
  background: linear-gradient(135deg, var(--primary-color), #3b82f6) !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  letter-spacing: 0.025em !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(17, 115, 212, 0.39) !important;
  color: white !important;
}

.iniciarSesion-btn:hover:not(:disabled) {
  transform: translateY(-1px) !important;
  box-shadow: 0 8px 25px 0 rgba(17, 115, 212, 0.5) !important;
  background: linear-gradient(135deg, var(--primary-hover), #2563eb) !important;
}

.iniciarSesion-btn:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

.btn-primary:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* Enlace de recuperación de contraseña */
.forgot-password-link {
  color: var(--primary-color) !important;
  text-decoration: none !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
}

.forgot-password-link:hover {
  color: var(--primary-hover) !important;
  text-decoration: underline !important;
}

.iniciarSesion-container.dark-theme .forgot-password-link {
  color: #60a5fa !important;
}

.iniciarSesion-container.dark-theme .forgot-password-link:hover {
  color: #3b82f6 !important;
}

/* Alertas - Mismas del admin */
.alert-danger {
  background: rgba(239, 68, 68, 0.1) !important;
  border: 1px solid rgba(239, 68, 68, 0.3) !important;
  color: #dc2626 !important;
  border-radius: 8px !important;
  font-size: 0.875rem !important;
  padding: 0.75rem 1rem !important;
}

.iniciarSesion-container.dark-theme .alert-danger {
  background: rgba(239, 68, 68, 0.15) !important;
  border-color: rgba(239, 68, 68, 0.4) !important;
  color: #fca5a5 !important;
}

/* Animaciones */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.iniciarSesion-card {
  animation: fadeInUp 0.6s ease-out;
}

/* Responsive */
@media (max-width: 768px) {
  .row {
    flex-direction: column;
  }
  
  .welcome-side,
  .iniciarSesion-side {
    min-height: 50vh;
    padding: 1.5rem;
  }
  
  .welcome-title {
    font-size: 2rem;
  }
  
  .iniciarSesion-logo-large {
    width: 200px;
  }
  
  .iniciarSesion-card {
    padding: 1.5rem;
  }

  .theme-toggle {
    top: 15px;
    right: 15px;
  }

  .theme-toggle-btn {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
}

/* Mejoras de accesibilidad */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus visible para navegación por teclado */
.theme-toggle-btn:focus-visible,
.btn-primary:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}
</style>



